import type { BlogPosting, WithContext, LiveBlogPosting } from 'schema-dts';

import type { LbPost, LbPart, LbOptions } from './types';
import { stripHtml, formatString } from './utils';

export const defaultBlogPostingItem = ({
  headline,
  articleBody,
  image,
  datePublished,
  dateModified,
  baseUrl,
  baseUefaImgUrl,
}: {
  headline: string;
  articleBody: string;
  image: string;
  datePublished: string;
  dateModified: string;
  baseUrl: string;
  baseUefaImgUrl: string;
}): BlogPosting => ({
  '@type': 'BlogPosting',
  headline: formatString(headline),
  articleBody: articleBody,
  image: image,
  datePublished: datePublished,
  dateModified: dateModified,
  publisher: {
    '@type': 'Organization',
    name: 'UEFA',
    url: baseUrl,
    logo: {
      '@type': 'ImageObject',
      url: `${baseUefaImgUrl}/imgml/2016/elements/footer/logo.png`,
      width: '60',
      height: '60',
    },
  },
  author: {
    '@type': 'Organization',
    name: 'UEFA',
    url: baseUrl,
    logo: {
      '@type': 'ImageObject',
      url: `${baseUefaImgUrl}/imgml/2016/elements/footer/logo.png`,
      width: '60',
      height: '60',
    },
  },
});

export const EditorialSchemaOrg = ({ options, data, item }: { options: LbOptions; data?: any; item: LbPost }) => {
  const headline = data?.Text;
  const articleBody = data?.Description;
  const image = data?.ImageUrl ?? `${options.baseUefaImgUrl}/imgml/2016/elements/footer/logo.png`;
  const datePublished = item.date;
  const dateModified = item.lastUpdateDate;
  if (!headline) return;

  return defaultBlogPostingItem({
    headline,
    articleBody,
    image,
    datePublished,
    dateModified,
    baseUrl: options.baseUrl,
    baseUefaImgUrl: options.baseUrl,
  });
};

export const LiveBlogSchemaOrg = ({
  options,
  matchDate,
  updates,
}: {
  options: LbOptions;
  matchDate?: Date;
  updates: BlogPosting[];
}): WithContext<LiveBlogPosting> => {
  if (!(matchDate instanceof Date && !isNaN(matchDate?.getTime()))) matchDate = new Date();
  const matchDateHrs = matchDate.getHours();
  const coverageStartDate = new Date(matchDate?.setHours(matchDateHrs - 5))?.toISOString();
  const coverageEndDate = new Date(matchDate?.setHours(matchDateHrs + 10))?.toISOString();
  return {
    '@context': 'https://schema.org',
    '@type': 'LiveBlogPosting',
    '@id': `${window.location.href}#${options?.blogId}`,
    url: window.location.href,
    coverageStartTime: coverageStartDate,
    coverageEndTime: coverageEndDate,
    isAccessibleForFree: true,
    headline: `${options?.match?.status?.toUpperCase() === 'LIVE' ? 'LIVE: ' : ''}${options?.blogHeadline}`,
    description: options?.blogDescription,
    keywords: [
      options.match?.competition?.displayName ?? 'UEFA.com',
      options.match?.homeTeam?.displayName,
      options.match?.awayTeam?.displayName,
    ].join(','),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': window.location.href,
    },
    datePublished: options?.blogPubDate ?? coverageStartDate,
    dateModified: updates?.length ? updates[0].dateModified : new Date().toISOString(),
    image: `${options?.baseUefaImgUrl}/imgml/2016/elements/footer/logo.png`,
    author: {
      '@type': 'Organization',
      name: 'UEFA',
      url: window.location.origin,
      logo: {
        '@type': 'ImageObject',
        url: `${options?.baseUefaImgUrl}/imgml/2016/elements/footer/logo.png`,
        width: '60',
        height: '60',
      },
    },
    publisher: {
      '@type': 'Organization',
      name: 'UEFA',
      url: window.location.origin,
      logo: {
        '@type': 'ImageObject',
        url: `${options?.baseUefaImgUrl}/imgml/2016/elements/footer/logo.png`,
        width: '60',
        height: '60',
      },
    },
    liveBlogUpdate: updates,
  };
};

export const PlainTextSchemaOrg = ({
  options,
  headline,
  articleBody,
  item,
}: {
  options: LbOptions;
  headline: string;
  articleBody: string;
  item: LbPost;
}): BlogPosting => {
  const image = `${options.baseUefaImgUrl}/imgml/2016/elements/footer/logo.png`;
  const datePublished = item.date;
  const dateModified = item.lastUpdateDate;
  if (!headline) return;
  if (typeof articleBody !== 'string') {
    articleBody = headline;
  }

  return defaultBlogPostingItem({
    headline,
    articleBody,
    image,
    datePublished,
    dateModified,
    baseUrl: options.baseUrl,
    baseUefaImgUrl: options.baseUrl,
  });
};

export const FreePhotoSchemaOrg = ({
  options,
  headline,
  image,
  item,
}: {
  options: LbOptions;
  headline: string;
  image: string;
  item: LbPost;
}): BlogPosting => {
  const datePublished = item.date;
  const dateModified = item.lastUpdateDate;
  const articleBody = headline;
  if (!headline) return;
  return defaultBlogPostingItem({
    headline,
    articleBody,
    image,
    datePublished,
    dateModified,
    baseUrl: options.baseUrl,
    baseUefaImgUrl: options.baseUrl,
  });
};

export const MatchEventSchemaOrg = ({
  options,
  headline,
  articleBody,
  item,
}: {
  options: LbOptions;
  headline: string;
  articleBody: string;
  item: LbPost;
}): BlogPosting => {
  const image = `${options.baseUefaImgUrl}/imgml/2016/elements/footer/logo.png`;
  const datePublished = item.date;
  const dateModified = item.lastUpdateDate;
  if (!headline) return;

  return defaultBlogPostingItem({
    headline,
    articleBody,
    image,
    datePublished,
    dateModified,
    baseUrl: options.baseUrl,
    baseUefaImgUrl: options.baseUrl,
  });
};

export const mappingFn = (options: LbOptions) => (item: LbPost) => {
  const type = item?.liveblogPostData?.lbPostType;
  if (type === 'EDITORIAL') {
    const part = item?.parts?.[0];
    if (part?.datasource === 'UefaEditorial') {
      const data = part?.data;
      return EditorialSchemaOrg({ options, data, item });
    }

    if (part?.datasource === 'Composer') {
      const freePhoto = item.parts.find((i: LbPart) => i.datasource === 'FreePhoto');
      if (item.parts.every((i: LbPart) => i.datasource === 'Composer')) {
        const headline = part?.data?.Text;
        const articleBody = item?.parts.length > 1 ? stripHtml(item?.parts?.[1].data?.Text) : stripHtml(headline);
        return PlainTextSchemaOrg({ options, headline, articleBody, item });
      }

      if (freePhoto) {
        const headline = part?.data?.Text;
        const image = freePhoto?.data.imgUrl.startsWith('//')
          ? `https:${freePhoto?.data.imgUrl}`
          : freePhoto?.data.imgUrl;
        return FreePhotoSchemaOrg({ options, headline, image, item });
      }
    }
  }

  if (type === 'MATCH_EVENT') {
    const part = item?.parts[0];
    const articleBody = stripHtml(part?.data?.Text);
    let headline = `${options.match?.competition?.displayName ?? ''}: ${options.match?.homeTeam?.displayName ?? ''}-${
      options.match?.awayTeam?.displayName ?? ''
    } ${item.liveblogPostData?.lbPostFSPEvent?.eventDisplayMinute ?? ''}`;
    headline = headline.trim() !== ': -' ? headline : articleBody;
    return MatchEventSchemaOrg({ options, headline, articleBody, item });
  }

  return null;
};
